import React, { Component, type ComponentType } from 'react';
import '@atlaskit/css-reset';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/index.tsx';
import { ff } from '@atlassian/jira-feature-flagging';
import DetailsApp, { type Props as DetailsPageSpaProps } from '@atlassian/jira-project-details/src';
import type { UpdateableDetails } from '@atlassian/jira-project-details/src/model/types';
import {
	getNextgenSettingsUrl,
	getProjectSettingsDetailsUrl,
} from '@atlassian/jira-project-settings-apps-common/src/urls';
import type { ProjectContext as ProjectContextType } from '@atlassian/jira-router-resources-project-context/src/services/project-context/types.tsx';
import type { TenantContext } from '@atlassian/jira-shared-types/src/tenant-context.tsx';
import { ProjectSubProductUpdater } from '@atlassian/jira-spa-apps-common/src/analytics-sub-product/project-sub-product-updater/index.tsx';
import AppContainer from '@atlassian/jira-spa-apps-project-settings-common/src/components/app-container';
import { ProjectSettingsController } from '@atlassian/jira-spa-apps-project-settings-common/src/components/routing-filter';
import { SharedSettingsDocumentTitle } from '@atlassian/jira-spa-apps-project-settings-common/src/shared-document-title';
import ProjectContext from '@atlassian/jira-spa-apps-project-settings-common/src/unified-project-context';
import { MarkProductStart } from '@atlassian/jira-spa-performance-breakdown/src/utils/mark-product-start/index.tsx';
import { TenantContextSubscriber } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import {
	type RouteContext,
	RouterActions,
	type RouterActionsType,
	withRouter,
} from '@atlassian/react-resource-router';

type Props = RouteContext;

const APP_ID = 'project-settings-apps.details-page';

// eslint-disable-next-line jira/react/no-class-components
export class DetailsPage extends Component<Props> {
	static renderApp(
		{
			tenantContext,
		}: {
			tenantContext: TenantContext;
		},
		component: ComponentType<DetailsPageSpaProps>,
		projectContext: ProjectContextType | null,
		updateProjectContext: (arg1: ProjectContextType) => void,
	) {
		const App: ComponentType<DetailsPageSpaProps> | null | undefined = component;

		if (!App || !projectContext || !tenantContext) {
			return null;
		}

		const onDetailsUpdated =
			({ push }: RouterActionsType) =>
			({ key, name }: UpdateableDetails) => {
				if (!projectContext) {
					return;
				}
				if (key !== projectContext.projectKey) {
					if (ff('hix-4818-adopt-modern-project-details-page-across-all-products_3zvy1')) {
						push(
							getProjectSettingsDetailsUrl(
								projectContext.projectType,
								key,
								projectContext.simplified,
							),
						);
					} else {
						push(`${getNextgenSettingsUrl(projectContext.projectType, key)}/details`);
					}
					return;
				}
				updateProjectContext({
					...projectContext,
					projectName: name,
				});
			};
		const content = (
			<RouterActions>
				{(historyActions) =>
					projectContext && (
						<>
							<SharedSettingsDocumentTitle
								projectName={projectContext.projectName}
								section="details"
								projectType={projectContext.projectType}
							/>
							<App
								isAdmin={tenantContext.isAdmin}
								projectId={+projectContext.projectId}
								projectType={projectContext.projectType}
								simplified={projectContext.simplified}
								baseUrl={tenantContext.baseUrl}
								locale={tenantContext.locale}
								onDetailsUpdated={onDetailsUpdated(historyActions)}
								appEditions={tenantContext.appEditions}
							/>
						</>
					)
				}
			</RouterActions>
		);

		return (
			<>
				<AppContainer baseUrl={tenantContext.baseUrl} projectKey={projectContext.projectKey}>
					{content}
				</AppContainer>
			</>
		);
	}

	renderContents() {
		const { projectKey } = this.props.match.params;

		return (
			<JSErrorBoundary id={APP_ID} packageName="projectSettingsDetails" teamName="helix">
				<TenantContextSubscriber>
					{({ tenantContext }) => (
						<ProjectContext>
							{({ projectContext, updateProjectContext }) => (
								<>
									{projectKey && <ProjectSubProductUpdater />}
									{DetailsPage.renderApp(
										{ tenantContext },
										DetailsApp,
										projectContext,
										updateProjectContext,
									)}
								</>
							)}
						</ProjectContext>
					)}
				</TenantContextSubscriber>
			</JSErrorBoundary>
		);
	}

	render() {
		const { projectKey } = this.props.match.params;
		return (
			<>
				<MarkProductStart />
				<ProjectSettingsController projectKey={projectKey}>
					{this.renderContents()}
				</ProjectSettingsController>
			</>
		);
	}
}

export default withRouter(DetailsPage);
